import { createStyles, makeStyles } from "@material-ui/core";
import {
    CandidateFeedback,
    CandidateJobInterviews,
    CircleIcon,
    DesktopPage,
    DesktopPageActions,
    DesktopPageSection,
    DesktopSideBar,
    DesktopSideBarPane,
    DesktopTabbedContent,
    Link,
    RelodeTheme,
    StatusLabel,
    Text,
} from "@relode/core";
import { JobCandidateStatus, UserRole } from "@relode/core/domain";
import * as React from "react";

import {
    JobManagerContactDetails,
    CandidateHighlights,
    CandidateHiredInfo,
    CandidateProfile,
    CandidateResumeDesktop,
    InterviewAvailability,
} from "../../../../../shared/components/candidate";
import { useCandidateContext } from "../../../_infra";
import { CandidateActionOptions, CandidateActions } from "../candidateActions";
import { CandidateDetailsProps } from "./candidateDetailsProps";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            circle: {
                fontSize: "0.5em",
                color: theme.palette.action.active,
                marginTop: theme.spacing(1),
                marginRight: theme.spacing(1),
                marginLeft: theme.spacing(1),
            },
            forLabel: {
                color: theme.palette.grey[500],
            },
            mediumAndDownHide: {
                [theme.breakpoints.down("md")]: {
                    display: "none",
                },
            },
        }),
    { name: "CandidateDetailsDesktop" }
);

export const CandidateDetailsDesktop = (props: CandidateDetailsProps) => {
    const {
        state: {
            data: { candidate, currentLeadId, currentJobId },
            ui: { isCandidateLoading },
        },
    } = useCandidateContext();
    const classes = useStyles({});

    const canViewInterviews =
        candidate.status === JobCandidateStatus.Interviewing ||
        (!!candidate.upcomingInterviews &&
            candidate.upcomingInterviews.length > 0) ||
        (!!candidate.pastInterviews && candidate.pastInterviews.length > 0);

    const {
        backUrl,
        addOrUpdateFeedback,
        toggleMessageComposer,
        toggleShareCandidate,
        handleSubmitInterview,
        handleSendManualInterviewNotification,
    } = props;

    return (
        <>
            <DesktopPage
                pageTitle={candidate.highlights.fullName}
                isBusy={isCandidateLoading}
                actions={
                    <DesktopPageActions
                        backUrl={backUrl ? backUrl : "/candidates"}
                        title={
                            candidate.highlights
                                ? candidate.highlights.fullName
                                : "n/a"
                        }
                        statusLabel={
                            candidate.status ? (
                                <StatusLabel
                                    title={candidate.status.description}
                                    jobCandidateStatus={candidate.status}
                                />
                            ) : null
                        }
                        subtitle={
                            <Text dataCy="candidate-subtitle">
                                <span className={classes.forLabel}>for:</span>{" "}
                                <Link
                                    to={{
                                        pathname: `/jobs/${candidate.job.jobId}`,
                                        state: {
                                            backUrl: `/candidates/${currentJobId}/${currentLeadId}`,
                                        },
                                    }}
                                >
                                    {candidate.job.jobName}
                                </Link>
                                <CircleIcon className={classes.circle} />{" "}
                                <span className={classes.mediumAndDownHide}>
                                    {candidate.job.teamName}
                                    <CircleIcon
                                        className={classes.circle}
                                    />{" "}
                                </span>
                                {candidate.job.location.locationName}
                            </Text>
                        }
                        pageActionOptions={
                            <CandidateActionOptions
                                shareAction={toggleShareCandidate}
                                messageAction={toggleMessageComposer}
                            />
                        }
                        pageActions={
                            <CandidateActions
                                addOrUpdateFeedback={addOrUpdateFeedback}
                                feedbackDetails={candidate.feedbackDetails}
                            />
                        }
                    ></DesktopPageActions>
                }
            >
                <DesktopTabbedContent defaultSection="highlights">
                    <DesktopPageSection label="highlights" id="highlights">
                        <CandidateHighlights
                            highlights={candidate.highlights}
                            qualifications={candidate.qualifications}
                            additionalInfo={candidate.additionalInfo}
                        />
                    </DesktopPageSection>

                    <DesktopPageSection label="resume" id="resume">
                        <CandidateResumeDesktop
                            resume={candidate.formattedResume}
                        />
                    </DesktopPageSection>

                    <DesktopPageSection label="feedback" id="feedback">
                        <CandidateFeedback
                            candidateFullName={candidate.highlights.fullName}
                            feedbackDetails={candidate.feedbackDetails}
                            addOrUpdateFeedback={addOrUpdateFeedback}
                        />
                    </DesktopPageSection>

                    {canViewInterviews ? (
                        <DesktopPageSection label="interviews" id="interviews">
                            <CandidateJobInterviews
                                candidateUserId={candidate.highlights.leadId}
                                candidateRoleName={"Candidate"}
                                candidateFullName={
                                    candidate.highlights.fullName
                                }
                                candidateFirstName={
                                    candidate.highlights.firstName
                                }
                                jobName={candidate.job.jobName}
                                jobId={candidate.job.jobId}
                                companyName={candidate.job.jobName}
                                companyBackground={candidate.job.teamBackground}
                                jobJobManager={{
                                    name: candidate.jobManager.fullName,
                                    // eslint-disable-next-line max-len
                                    role: UserRole.fromName(
                                        candidate.jobManager.roleName
                                    ),
                                    email: candidate.jobManager.email,
                                    userId: candidate.jobManager.userId,
                                    phoneNumber:
                                        candidate.jobManager.phoneNumber,
                                }}
                                upcomingInterviews={
                                    candidate.upcomingInterviews
                                }
                                pastInterviews={candidate.pastInterviews}
                                canEditInterview={
                                    candidate.canViewCandidateActions &&
                                    candidate.status.id ==
                                        JobCandidateStatus.Interviewing.id
                                }
                                canAddInterview={candidate.canAddInterview}
                                isCandidateLoading={isCandidateLoading}
                                onSubmitInterview={handleSubmitInterview}
                                handleSendManualInterviewNotification={
                                    handleSendManualInterviewNotification
                                }
                            />
                        </DesktopPageSection>
                    ) : null}
                    {candidate?.hiredDetails?.startDate ? (
                        <DesktopPageSection label="hire info" id="hireInfo">
                            <CandidateHiredInfo
                                hiredInfo={candidate.hiredDetails}
                                candidateJobType={candidate.job.jobType}
                                candidate={candidate}
                            />
                        </DesktopPageSection>
                    ) : null}
                </DesktopTabbedContent>
                <DesktopSideBar>
                    <DesktopSideBarPane>
                        <CandidateProfile
                            highlights={candidate.highlights}
                            additionalInfo={candidate.additionalInfo}
                        />
                    </DesktopSideBarPane>

                    <DesktopSideBarPane>
                        <InterviewAvailability
                            availability={candidate.availability}
                            additionalInfo={candidate.additionalInfo}
                        />
                    </DesktopSideBarPane>

                    <DesktopSideBarPane>
                        <JobManagerContactDetails
                            title="questions?"
                            jobManagerContact={candidate.jobManager}
                            leadId={candidate.highlights.leadId}
                            leadName={candidate.highlights.fullName}
                            jobId={candidate.job.jobId}
                            jobName={candidate.job.jobName}
                            contactActionVariant="button"
                        />
                    </DesktopSideBarPane>
                </DesktopSideBar>
            </DesktopPage>
        </>
    );
};
